/**
 * SeKa Bucket Bundle Configurator
 * ================================
 * 3-Löffel Bundle Konfigurator für Shopify
 * 
 * Features:
 * - Maschinengewicht-Auswahl
 * - Exakt 3 Löffel auswählen
 * - Live-Preisberechnung mit Rabatt
 * - Filterchips nach Typ
 * - AJAX Add-to-Cart
 * - VAT-Switcher Support
 */

(function() {
  'use strict';

  const SekaBucketConfigurator = {
    // Configuration
    config: {
      maxProducts: 3,
      discountPercent: 10,
      vatRate: 19,
      discountCode: '',
      redirectAfterAdd: false,
      showDrawer: true
    },

    // State
    state: {
      selectedWeight: null,
      weightLabel: '',
      weightSuffix: '',
      selectedProducts: [],
      activeFilter: 'all',
      isVatNet: false,
      isLoading: false
    },

    // DOM Elements
    elements: {},

    /**
     * Initialize the configurator
     */
    init() {
      const container = document.getElementById('seka-bucket-bundle');
      if (!container) return;

      this.container = container;
      this.loadConfig();
      this.cacheElements();
      this.bindEvents();
      this.checkVatMode();
      
      console.log('[SekaBucketConfigurator] Initialized');
    },

    /**
     * Load configuration from data attributes
     */
    loadConfig() {
      this.config.discountPercent = parseFloat(this.container.dataset.discountPercent) || 10;
      this.config.vatRate = parseFloat(this.container.dataset.vatRate) || 19;
      this.config.discountCode = this.container.dataset.discountCode || '';
      this.config.redirectAfterAdd = this.container.dataset.redirectAfterAdd === 'true';
      this.config.showDrawer = this.container.dataset.showDrawer !== 'false';
    },

    /**
     * Cache DOM elements
     */
    cacheElements() {
      this.elements = {
        // Weight selection
        weightGrid: document.getElementById('weight-grid'),
        weightDisplay: document.getElementById('weight-display'),
        selectedWeightLabel: document.getElementById('selected-weight-label'),
        changeWeightBtn: document.getElementById('change-weight-btn'),
        weightButtons: this.container.querySelectorAll('.seka-bundle__weight-btn'),

        // Main area
        bundleMain: document.getElementById('bundle-main'),

        // Progress
        progressFill: document.getElementById('progress-fill'),
        selectedCount: document.getElementById('selected-count'),

        // Filters
        filterChips: document.getElementById('filter-chips'),

        // Products
        productGrid: document.getElementById('product-grid'),
        productCards: this.container.querySelectorAll('.seka-bundle__product-card'),
        maxWarning: document.getElementById('max-warning'),

        // Summary
        summaryItems: document.getElementById('summary-items'),
        summaryCount: document.getElementById('summary-count'),
        subtotalDisplay: document.getElementById('subtotal-display'),
        discountDisplay: document.getElementById('discount-display'),
        totalDisplay: document.getElementById('total-display'),
        savingsDisplay: document.getElementById('savings-display'),
        savingsAmount: document.getElementById('savings-amount'),

        // CTA
        addToCartBtn: document.getElementById('add-to-cart-btn'),
        ctaHint: document.getElementById('cta-hint'),

        // Toast
        successToast: document.getElementById('success-toast'),
        toastMessage: document.getElementById('toast-message')
      };
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Weight selection
      this.elements.weightButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectWeight(btn));
      });

      // Change weight button
      if (this.elements.changeWeightBtn) {
        this.elements.changeWeightBtn.addEventListener('click', () => this.resetWeight());
      }

      // Filter chips
      if (this.elements.filterChips) {
        this.elements.filterChips.addEventListener('click', (e) => {
          const chip = e.target.closest('.seka-bundle__filter-chip');
          if (chip) this.setFilter(chip.dataset.filter);
        });
      }

      // Product cards
      this.elements.productCards.forEach(card => {
        card.addEventListener('click', (e) => {
          // Don't toggle if clicking variant select or remove button
          if (e.target.closest('.seka-bundle__variant-select')) return;
          if (e.target.closest('.seka-bundle__summary-remove')) return;
          this.toggleProduct(card);
        });
      });

      // Variant selectors
      this.container.querySelectorAll('.seka-bundle__variant-select').forEach(select => {
        select.addEventListener('change', (e) => {
          e.stopPropagation();
          this.updateVariantSelection(e.target);
        });
      });

      // Add to cart
      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.addEventListener('click', () => this.addToCart());
      }

      // VAT mode changes
      document.addEventListener('vatModeChanged', (e) => {
        this.state.isVatNet = e.detail?.mode === 'net';
        this.updatePricing();
      });

      // MutationObserver for body class changes (VAT)
      const observer = new MutationObserver(() => {
        this.checkVatMode();
      });
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    },

    /**
     * Check current VAT mode
     */
    checkVatMode() {
      this.state.isVatNet = document.body.classList.contains('vat-mode-net');
    },

    /**
     * Select machine weight
     */
    selectWeight(btn) {
      // Update state
      this.state.selectedWeight = btn.dataset.weight;
      this.state.weightLabel = btn.dataset.weightLabel;
      this.state.weightSuffix = btn.dataset.suffix;

      // Update UI
      this.elements.weightButtons.forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      // Show weight display and hide grid
      if (this.elements.weightDisplay) {
        this.elements.weightDisplay.style.display = 'flex';
        this.elements.selectedWeightLabel.textContent = this.state.weightLabel;
      }
      if (this.elements.weightGrid) {
        this.elements.weightGrid.style.display = 'none';
      }

      // Show main area
      if (this.elements.bundleMain) {
        this.elements.bundleMain.style.display = 'grid';
      }

      // Filter products by tonnage class
      this.filterByTonnage();

      // Reset products when weight changes
      this.resetProducts();
      this.filterProducts();
      this.updateCTAState();
      this.checkVisibleProducts();

      // Scroll to products
      this.elements.bundleMain?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /**
     * Reset weight selection
     */
    resetWeight() {
      this.state.selectedWeight = null;
      this.state.weightLabel = '';
      this.state.weightSuffix = '';

      // Update UI
      this.elements.weightButtons.forEach(b => b.classList.remove('is-selected'));
      
      if (this.elements.weightDisplay) {
        this.elements.weightDisplay.style.display = 'none';
      }
      if (this.elements.weightGrid) {
        this.elements.weightGrid.style.display = 'grid';
      }
      if (this.elements.bundleMain) {
        this.elements.bundleMain.style.display = 'none';
      }

      // Reset tonnage filter
      this.elements.productCards.forEach(card => {
        card.classList.remove('is-hidden-tonnage');
      });

      this.resetProducts();
      this.updateCTAState();
    },

    /**
     * Set active filter
     */
    setFilter(filter) {
      this.state.activeFilter = filter;

      // Update chip UI
      this.elements.filterChips.querySelectorAll('.seka-bundle__filter-chip').forEach(chip => {
        chip.classList.toggle('is-active', chip.dataset.filter === filter);
      });

      // Filter products
      this.filterProducts();
    },

    /**
     * Filter products based on type (respects tonnage filter)
     */
    filterProducts() {
      const filter = this.state.activeFilter;

      this.elements.productCards.forEach(card => {
        const type = card.dataset.productType;
        const matchesType = filter === 'all' || type === filter;
        const isHiddenByTonnage = card.classList.contains('is-hidden-tonnage');
        
        // Only show if type matches AND not hidden by tonnage
        card.classList.toggle('is-hidden', !matchesType || isHiddenByTonnage);
      });

      this.checkVisibleProducts();
    },

    /**
     * Filter products by selected tonnage class
     */
    filterByTonnage() {
      const suffix = this.state.weightSuffix;
      if (!suffix) return;

      const requiredTag = `tklasse_${suffix}`;

      this.elements.productCards.forEach(card => {
        const tonnageTags = (card.dataset.tonnageTags || '').toLowerCase();
        const hasMatchingTonnage = tonnageTags.includes(requiredTag.toLowerCase());
        
        card.classList.toggle('is-hidden-tonnage', !hasMatchingTonnage);
      });
    },

    /**
     * Check if any products are visible and show/hide empty message
     */
    checkVisibleProducts() {
      const visibleCards = Array.from(this.elements.productCards).filter(card => {
        return !card.classList.contains('is-hidden') && 
               !card.classList.contains('is-hidden-tonnage');
      });

      const noMatchingEl = document.getElementById('no-matching-products');
      if (noMatchingEl) {
        noMatchingEl.style.display = visibleCards.length === 0 ? 'flex' : 'none';
      }
    },

    /**
     * Toggle product selection
     * Now uses variantId as unique key to allow multiple variants of the same product
     */
    toggleProduct(card) {
      const variantId = card.dataset.variantId;
      
      // Check if this VARIANT is already selected (not just the product)
      const existingSelection = this.state.selectedProducts.find(p => p.variantId === variantId);
      const isSelected = existingSelection !== undefined;
      
      // Check availability - first from card, then from selected variant
      let isAvailable = card.dataset.available !== 'false';
      
      // If there's a variant select, check the selected variant's availability
      const variantSelect = card.querySelector('.seka-bundle__variant-select');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption && selectedOption.dataset.available === 'false') {
          isAvailable = false;
        }
      }
      
      // Don't allow selection if product/variant is not available
      if (!isAvailable) {
        this.showToast('Diese Variante ist nicht verfügbar. Bitte wähle eine andere.', true);
        return;
      }

      if (isSelected) {
        // Deselect this variant
        this.deselectProduct(variantId, card);
      } else {
        // Check if max reached
        if (this.state.selectedProducts.length >= this.config.maxProducts) {
          this.showMaxWarning();
          return;
        }
        // Select
        this.selectProduct(card);
      }

      this.updateProgress();
      this.updateSummary();
      this.updatePricing();
      this.updateCTAState();
      this.hideMaxWarning();
    },

    /**
     * Select a product
     * Uses variantId as unique key to allow multiple variants of the same product
     */
    selectProduct(card) {
      const variantId = card.dataset.variantId;
      
      // Check if this exact variant is already selected (prevent duplicates)
      if (this.state.selectedProducts.some(p => p.variantId === variantId)) {
        this.showToast('Diese Variante ist bereits ausgewählt.', true);
        return;
      }
      
      // Get variant info if available
      const variantSelect = card.querySelector('.seka-bundle__variant-select');
      let variantTitle = '';
      
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        variantTitle = selectedOption.dataset.title || selectedOption.text.split(' – ')[0];
      }

      const productData = {
        id: card.dataset.productId,
        variantId: variantId,
        variantTitle: variantTitle,
        title: card.dataset.productTitle,
        image: card.dataset.productImage,
        priceGross: parseInt(card.dataset.priceGross, 10),
        priceNet: parseInt(card.dataset.priceNet, 10),
        type: card.dataset.productType,
        element: card
      };

      this.state.selectedProducts.push(productData);
      card.classList.add('is-selected');

      // Disable other cards if max reached
      if (this.state.selectedProducts.length >= this.config.maxProducts) {
        this.elements.productCards.forEach(c => {
          if (!c.classList.contains('is-selected')) {
            c.classList.add('is-disabled');
          }
        });
      }
    },

    /**
     * Update variant selection
     */
    updateVariantSelection(select) {
      const productId = select.dataset.productId;
      const card = this.container.querySelector(`[data-product-id="${productId}"]`);
      if (!card) return;

      const selectedOption = select.options[select.selectedIndex];
      const newVariantId = select.value;
      const newPriceGross = parseInt(selectedOption.dataset.priceGross, 10);
      const newPriceNet = parseInt(selectedOption.dataset.priceNet, 10);
      const variantTitle = selectedOption.dataset.title || selectedOption.text.split(' – ')[0];
      const isAvailable = selectedOption.dataset.available !== 'false';

      // Update card data attributes
      card.dataset.variantId = newVariantId;
      card.dataset.priceGross = newPriceGross;
      card.dataset.priceNet = newPriceNet;

      // Update displayed price on card
      const priceGrossEl = card.querySelector('.seka-bundle__price-gross');
      const priceNetEl = card.querySelector('.seka-bundle__price-net');
      
      if (priceGrossEl) {
        priceGrossEl.textContent = this.formatMoney(newPriceGross);
      }
      if (priceNetEl) {
        priceNetEl.textContent = this.formatMoney(newPriceNet);
      }
      
      // Handle variant availability visual feedback
      if (!isAvailable) {
        card.classList.add('is-variant-unavailable');
      } else {
        card.classList.remove('is-variant-unavailable');
      }
      
      // Update availability display based on preorder status
      const availabilityContainer = card.querySelector('[data-availability-container]');
      if (availabilityContainer) {
        const isPreorder = selectedOption.dataset.isPreorder === 'true';
        const leadTime = selectedOption.dataset.leadTime || 'Vorbestellbar';
        
        let availabilityHtml = '';
        
        if (!isAvailable) {
          availabilityHtml = `
            <span class="seka-bundle__availability seka-bundle__availability--out-of-stock">
              Nicht verfügbar
            </span>
          `;
        } else if (isPreorder) {
          availabilityHtml = `
            <span class="seka-bundle__availability seka-bundle__availability--preorder">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              ${leadTime}
            </span>
          `;
        } else {
          availabilityHtml = `
            <span class="seka-bundle__availability seka-bundle__availability--in-stock">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Auf Lager
            </span>
          `;
        }
        
        availabilityContainer.innerHTML = availabilityHtml;
      }

      // NEW LOGIC: Check if the NEW variant is already in selection
      const isNewVariantSelected = this.state.selectedProducts.some(p => p.variantId === newVariantId);
      
      // Update card's is-selected class based on whether THIS variant is selected
      // This allows selecting multiple variants of the same product
      card.classList.toggle('is-selected', isNewVariantSelected);
      
      // Handle disabled state
      if (this.state.selectedProducts.length >= this.config.maxProducts && !isNewVariantSelected) {
        card.classList.add('is-disabled');
      } else {
        card.classList.remove('is-disabled');
      }
      
      // NOTE: We do NOT update existing selections when variant changes.
      // The user must click the card again to add the new variant as a separate selection.
      // This allows selecting multiple variants (e.g., 300mm AND 500mm) from the same product.
    },

    /**
     * Deselect a product by variantId
     * Now uses variantId instead of productId to allow multiple variants of same product
     */
    deselectProduct(variantId, card) {
      this.state.selectedProducts = this.state.selectedProducts.filter(p => p.variantId !== variantId);
      card.classList.remove('is-selected');

      // Re-enable all cards
      this.elements.productCards.forEach(c => {
        c.classList.remove('is-disabled');
      });
    },

    /**
     * Reset all products
     */
    resetProducts() {
      this.state.selectedProducts = [];
      this.elements.productCards.forEach(card => {
        card.classList.remove('is-selected', 'is-disabled');
      });
      this.updateProgress();
      this.updateSummary();
      this.updatePricing();
    },

    /**
     * Update progress indicator
     */
    updateProgress() {
      const count = this.state.selectedProducts.length;
      const percent = (count / this.config.maxProducts) * 100;

      if (this.elements.progressFill) {
        this.elements.progressFill.style.width = `${percent}%`;
      }
      if (this.elements.selectedCount) {
        this.elements.selectedCount.textContent = count;
      }
    },

    /**
     * Update summary items
     */
    updateSummary() {
      if (!this.elements.summaryItems) return;

      const slots = this.elements.summaryItems.querySelectorAll('.seka-bundle__summary-item');
      
      slots.forEach((slot, index) => {
        const product = this.state.selectedProducts[index];
        
        if (product) {
          // Generate product code
          const code = this.generateCode(product.type);
          const price = this.state.isVatNet ? product.priceNet : product.priceGross;
          
          // Variant subtitle
          const variantSubtitle = product.variantTitle 
            ? `<small class="seka-bundle__summary-variant">${product.variantTitle}</small>` 
            : '';

          slot.classList.add('is-filled');
          slot.innerHTML = `
            <span class="seka-bundle__summary-slot">${index + 1}</span>
            <div class="seka-bundle__summary-product">
              <img src="${product.image}" alt="${product.title}">
              <div class="seka-bundle__summary-product-info">
                <span class="seka-bundle__summary-product-title">${product.title}</span>
                ${variantSubtitle}
                <span class="seka-bundle__summary-product-code">${code}</span>
              </div>
              <span class="seka-bundle__summary-product-price">${this.formatMoney(price)}</span>
            </div>
            <button type="button" class="seka-bundle__summary-remove" data-variant-id="${product.variantId}" aria-label="Entfernen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          `;

          // Bind remove button - now uses variantId
          const removeBtn = slot.querySelector('.seka-bundle__summary-remove');
          if (removeBtn) {
            removeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              const variantId = removeBtn.dataset.variantId;
              // Find the product with this variantId and get its card element
              const selectedProduct = this.state.selectedProducts.find(p => p.variantId === variantId);
              if (selectedProduct && selectedProduct.element) {
                this.toggleProduct(selectedProduct.element);
              }
            });
          }
        } else {
          slot.classList.remove('is-filled');
          slot.innerHTML = `
            <span class="seka-bundle__summary-slot">${index + 1}</span>
            <span class="seka-bundle__summary-empty-text">Löffel ${index + 1} wählen...</span>
          `;
        }
      });

      // Update count
      if (this.elements.summaryCount) {
        this.elements.summaryCount.textContent = `${this.state.selectedProducts.length} / ${this.config.maxProducts}`;
      }
    },

    /**
     * Generate product code (e.g., TL2635)
     */
    generateCode(type) {
      return `${type}${this.state.weightSuffix}`;
    },

    /**
     * Update pricing display
     */
    updatePricing() {
      const products = this.state.selectedProducts;
      
      // Calculate subtotal
      let subtotal = 0;
      products.forEach(p => {
        subtotal += this.state.isVatNet ? p.priceNet : p.priceGross;
      });

      // Calculate discount
      const discountAmount = Math.round(subtotal * (this.config.discountPercent / 100));
      const total = subtotal - discountAmount;

      // Update displays
      if (this.elements.subtotalDisplay) {
        this.elements.subtotalDisplay.textContent = this.formatMoney(subtotal);
      }
      if (this.elements.discountDisplay) {
        this.elements.discountDisplay.textContent = `-${this.formatMoney(discountAmount)}`;
      }
      if (this.elements.totalDisplay) {
        this.elements.totalDisplay.textContent = this.formatMoney(total);
      }

      // Show/hide savings
      if (this.elements.savingsDisplay && this.elements.savingsAmount) {
        if (products.length === this.config.maxProducts && discountAmount > 0) {
          this.elements.savingsDisplay.style.display = 'flex';
          this.elements.savingsAmount.textContent = this.formatMoney(discountAmount);
        } else {
          this.elements.savingsDisplay.style.display = 'none';
        }
      }
    },

    /**
     * Update CTA button state
     */
    updateCTAState() {
      const hasWeight = !!this.state.selectedWeight;
      const hasAllProducts = this.state.selectedProducts.length === this.config.maxProducts;
      const canAdd = hasWeight && hasAllProducts && !this.state.isLoading;

      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.disabled = !canAdd;
      }

      if (this.elements.ctaHint) {
        if (!hasWeight) {
          this.elements.ctaHint.textContent = 'Wähle zuerst ein Maschinengewicht.';
        } else if (!hasAllProducts) {
          const remaining = this.config.maxProducts - this.state.selectedProducts.length;
          this.elements.ctaHint.textContent = `Wähle noch ${remaining} Löffel aus.`;
        } else {
          this.elements.ctaHint.textContent = 'Bereit! Klicke auf den Button, um das Bundle hinzuzufügen.';
        }
      }
    },

    /**
     * Show max products warning
     */
    showMaxWarning() {
      if (this.elements.maxWarning) {
        this.elements.maxWarning.style.display = 'flex';
        setTimeout(() => this.hideMaxWarning(), 4000);
      }
    },

    /**
     * Hide max products warning
     */
    hideMaxWarning() {
      if (this.elements.maxWarning) {
        this.elements.maxWarning.style.display = 'none';
      }
    },

    /**
     * Add bundle to cart
     */
    async addToCart() {
      if (this.state.isLoading) return;
      if (this.state.selectedProducts.length !== this.config.maxProducts) return;
      if (!this.state.selectedWeight) return;

      this.state.isLoading = true;
      this.elements.addToCartBtn?.classList.add('is-loading');

      try {
        // Build items array
        const items = this.state.selectedProducts.map(product => ({
          id: parseInt(product.variantId, 10),
          quantity: 1,
          properties: {
            'Maschinengewicht': this.state.weightLabel,
            'Bundle': '3-Löffel Paket',
            'Code': this.generateCode(product.type)
          }
        }));

        // POST to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();
        console.log('[SekaBucketConfigurator] Added to cart:', data);

        // Show success
        this.showToast('Bundle wurde zum Warenkorb hinzugefügt!');

        // Dispatch event
        document.dispatchEvent(new CustomEvent('bundleAddedToCart', {
          detail: {
            items: data.items || data,
            weight: this.state.weightLabel,
            products: this.state.selectedProducts
          }
        }));

        // Update cart count
        this.updateCartCount();

        // Handle redirect or drawer
        if (this.config.discountCode) {
          // Redirect with discount code
          window.location.href = `/discount/${this.config.discountCode}?redirect=/cart`;
        } else if (this.config.redirectAfterAdd) {
          window.location.href = '/cart';
        } else if (this.config.showDrawer) {
          // Refresh cart drawer contents and open it
          if (window.SeKaCartDrawer) {
            window.SeKaCartDrawer.refreshCart();
            window.SeKaCartDrawer.open();
          } else {
            // Fallback: dispatch events and click cart icon
            document.dispatchEvent(new CustomEvent('cart:open'));
            document.dispatchEvent(new CustomEvent('openCartDrawer'));
            const cartTrigger = document.querySelector('[data-cart-toggle], .cart-drawer-trigger, .header__cart-button');
            if (cartTrigger) cartTrigger.click();
          }
        }

      } catch (error) {
        console.error('[SekaBucketConfigurator] Error adding to cart:', error);
        this.showToast('Fehler beim Hinzufügen zum Warenkorb.', true);
      } finally {
        this.state.isLoading = false;
        this.elements.addToCartBtn?.classList.remove('is-loading');
      }
    },

    /**
     * Update cart count in header
     */
    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Update various cart count elements
        const countElements = document.querySelectorAll(
          '.cart-count, .cart-count-bubble, [data-cart-count], .header__cart-count'
        );
        countElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
          }
        });
      } catch (error) {
        console.warn('[SekaBucketConfigurator] Could not update cart count');
      }
    },

    /**
     * Show toast notification
     */
    showToast(message, isError = false) {
      if (!this.elements.successToast) return;

      if (this.elements.toastMessage) {
        this.elements.toastMessage.textContent = message;
      }

      this.elements.successToast.style.background = isError ? '#EF4444' : '#22C55E';
      this.elements.successToast.classList.add('is-visible');

      setTimeout(() => {
        this.elements.successToast.classList.remove('is-visible');
      }, 4000);
    },

    /**
     * Format money (cents to EUR)
     */
    formatMoney(cents) {
      const euros = (cents / 100).toFixed(2);
      return euros.replace('.', ',') + ' €';
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SekaBucketConfigurator.init());
  } else {
    SekaBucketConfigurator.init();
  }

  // Export to global scope
  window.SekaBucketConfigurator = SekaBucketConfigurator;

})();